import React, { useEffect, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { cancelSignatureRequestById, getSignatureInboxRequests } from "../../../services/fileServices/signatures/SignatureService";
import Form from "react-bootstrap/Form";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "./DateRangePicker";
import { convertUtcToLocalDate } from "../../../utilities/dateUtils";
import moment from "moment";
import "../../../assets/css/drive.css";
import { toast } from "react-toastify";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";

interface PaginationDto {
  pageNumber: number;
  pageSize: number;
}

interface InboxTableProps {
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  statusFilter: string;
  pagination: PaginationDto;
  onTotalCountChange: (count: number) => void;
  handleDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
}

interface SortState {
  sortColumn: string;
  sortDirection: string;
}

interface InboxItem {
  fileId?: string;
  fileName: string;
  emailFrom: string;
  status: string;
  requestToken: string;
  createdAt: string;
  expiresAt: string;
  signatureRequestUrl?: string;
  signedAt?: string; // May be present if status is completed
  signatureBoxID : string[];
}

export default function InboxTable({
  searchTerm: externalSearchTerm,
  startDate,
  endDate,
  statusFilter: externalStatusFilter,
  pagination,
  onTotalCountChange,
  handleDateRangeChange,
}: InboxTableProps) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [allData, setAllData] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [backendTotalCount, setBackendTotalCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(externalSearchTerm);
const [reload, setReload] = useState<boolean>(true);

  const [sort, setSort] = useState<SortState>({
    sortColumn: "",
    sortDirection: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(externalSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [externalSearchTerm]);

  useEffect(() => {
    fetchInboxRequests();
    // eslint-disable-next-line
  }, [currentUser, debouncedSearchTerm, sort, pagination.pageNumber, pagination.pageSize, startDate, endDate, reload]);

  const fetchInboxRequests = async () => {
    setLoading(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token || !currentUser?.uid) return;

      // Fetch data with pagination from backend
      const data = await getSignatureInboxRequests(
        token,
        currentUser.uid,
        pagination.pageNumber,
        pagination.pageSize,
        debouncedSearchTerm,
        sort.sortColumn,
        sort.sortDirection
      );

      console.log("Fetched inbox data:", data?.items); // Debug log
      setAllData(data?.items || []);
      setBackendTotalCount(data?.totalCount || 0);
    } catch (err) {
      console.error("Error fetching inbox requests:", err);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterDataByDateRange = (data: InboxItem[]): InboxItem[] => {
    if (!startDate && !endDate) return data;

    return data.filter((item) => {
      // Use createdAt for filtering (this is the received date)
      if (!item.createdAt || item.createdAt === "-") return false;

      try {
        const receivedDate = new Date(item.createdAt);
        receivedDate.setHours(0, 0, 0, 0);

        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return receivedDate >= start && receivedDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return receivedDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return receivedDate <= end;
        }
      } catch (error) {
        console.error("Error parsing date:", item.createdAt);
        return false;
      }

      return true;
    });
  };

  // Filter data by status
  const filterDataByStatus = (data: InboxItem[]): InboxItem[] => {
    if (!externalStatusFilter || externalStatusFilter === "All Status") {
      return data;
    }

    // Map dropdown status values to data status values
    const statusMap: Record<string, string> = {
      Pending: "pending",
      Completed: "completed",
      Rejected: "rejected", // Adjust if needed based on actual data
    };

    const filterStatus =
      statusMap[externalStatusFilter] || externalStatusFilter.toLowerCase();

    return data.filter((item) => {
      return item.status.toLowerCase() === filterStatus.toLowerCase();
    });
  };

  // Apply date range and status filters (search is handled by backend)
  const dateFilteredData = filterDataByDateRange(allData);
  const filteredData = filterDataByStatus(dateFilteredData);

  // Use filtered data directly (pagination is handled by backend)
  const paginatedData = filteredData;

  // Calculate pending count for badge (always count pending items, regardless of status filter)
  const pendingCount = dateFilteredData.filter((item) => 
    item.status?.toLowerCase() === "pending"
  ).length;

  // Update total count for pagination
  // Note: When date filtering is active (client-side), the total count might not be perfectly accurate
  // since we're only filtering the current page. This is a limitation of client-side date filtering.
  useEffect(() => {
    // Use backend totalCount as base (it reflects search/status filtering done by backend)
    // When date filtering is active, the count might be approximate
    onTotalCountChange(backendTotalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendTotalCount]);

  console.log(
    dateFilteredData,
    "dateFilteredData",
    filteredData,
    "Total filtered count:",
    filteredData.length,
    "Backend total count:",
    backendTotalCount,
    "Pending count:",
    pendingCount
  );

  const sortByColumn = (sortCol: string) => {
    if (sort.sortColumn === sortCol) {
      if (sort.sortDirection === "desc") {
        setSort({ sortColumn: sortCol, sortDirection: "asc" });
      } else {
        setSort({ sortColumn: sortCol, sortDirection: "desc" });
      }
    } else {
      setSort({ sortColumn: sortCol, sortDirection: "asc" });
    }
  };

const handleClickCancel = async (signatureBoxIds: string[]) => {
  if (!signatureBoxIds || signatureBoxIds.length === 0) {
    toast.error("No signature boxes selected. Cannot cancel.");
    return;
  }

  const isConfirm = await showConfirmationDialog(
    "Cancel Signature Request?",
    "This will cancel the signature request and unlock the document. This cannot be undone.",
    "Yes, cancel it",
    "Cancel"
  );

  if (!isConfirm) return;

  try {
    setLoading(true);

    const token = await currentUser?.getIdToken();
    if (!token) {
      toast.error("Authentication failed.");
      return;
    }

    const results = await Promise.all(
      signatureBoxIds.map(id =>
        cancelSignatureRequestById(token, id)
      )
    );

    const failed = results.filter(r => !r.success);

    if (failed.length === 0) {
      toast.success("All signature requests cancelled successfully!");
    } else {
      toast.error(
        `${failed.length} of ${signatureBoxIds.length} signature requests failed to cancel.`
      );
    }

    setReload(!reload);
  } catch (error) {
    console.error("Error cancelling signature request:", error);
    toast.error("Failed to cancel signature request");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="signature-table-wrapper">
      {/* Date Range Picker */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      ></div>

      {/* Table Section */}
      <div className="signature-table-container">
      <table className="drive-table min-w-[1100px] table" style={{tableLayout: 'fixed'}}>
          <thead className="table-header-css">
            <tr>
              <th
                className="sortable-header"
                onClick={() => sortByColumn("fileName")}
              >
                TITLE
                <span className="sort-icon">
                  {sort.sortColumn === "fileName"
                    ? sort.sortDirection === "asc"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="sortable-header"
                onClick={() => sortByColumn("emailFrom")}
              >
                SENDER
                <span className="sort-icon">
                  {sort.sortColumn === "emailFrom"
                    ? sort.sortDirection === "asc"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="sortable-header"
                onClick={() => sortByColumn("status")}
              >
                STATUS
                <span className="sort-icon">
                  {sort.sortColumn === "status"
                    ? sort.sortDirection === "asc"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="sortable-header"
                onClick={() => sortByColumn("createdAt")}
              >
                RECEIVED DATE
                <span className="sort-icon">
                  {sort.sortColumn === "createdAt"
                    ? sort.sortDirection === "asc"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="sortable-header"
                onClick={() => sortByColumn("signedAt")}
              >
                SIGNED AT
                <span className="sort-icon">
                  {sort.sortColumn === "signedAt"
                    ? sort.sortDirection === "asc"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody
            className={paginatedData.length >= 10 && pagination.pageSize === 10 ? "page-size-10" : ""}
          >
            {loading ? (
              <tr>
                <td colSpan={6} className="no-records">
                  <Spinner animation="border" size="sm" /> Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-records">
                  <strong>Nothing in inbox</strong>
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={item.fileId || index}
                  className={`signature-table-row ${
                    paginatedData.length >= 10 && pagination.pageSize === 10 ? "row-height-10" : ""
                  }`}
                >
                  <td>
                    <div className="file-name-cell">
                      <div className="file-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 17 17"
                          fill="none"
                        >
                          <path
                            d="M10.1056 1.34729H4.04264C3.68531 1.34729 3.34261 1.48924 3.08994 1.74191C2.83726 1.99459 2.69531 2.33729 2.69531 2.69462V13.4733C2.69531 13.8306 2.83726 14.1733 3.08994 14.426C3.34261 14.6787 3.68531 14.8206 4.04264 14.8206H12.1266C12.484 14.8206 12.8267 14.6787 13.0793 14.426C13.332 14.1733 13.474 13.8306 13.474 13.4733V4.71562L10.1056 1.34729Z"
                            stroke="#8B5CF6"
                            strokeWidth="1.0105"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9.42969 1.34729V4.04195C9.42969 4.39929 9.57164 4.74199 9.82431 4.99466C10.077 5.24733 10.4197 5.38928 10.777 5.38928H13.4717"
                            stroke="#8B5CF6"
                            strokeWidth="1.0105"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.736 6.06299H5.38867"
                            stroke="#8B5CF6"
                            strokeWidth="1.0105"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.778 8.75769H5.38867"
                            stroke="#8B5CF6"
                            strokeWidth="1.0105"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.778 11.4523H5.38867"
                            stroke="#8B5CF6"
                            strokeWidth="1.0105"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="inbox-file-info">
                        <span className="document-filename shortDescription" title={item.fileName}>{item.fileName}</span>
                        <p className="inbox-file-from"></p>
                      </div>
                    </div>
                  </td>
                  <td className="shortDescription" title={item.emailFrom}>{item.emailFrom}</td>
                  <td>
                    {/* <span className={`status-badge status-${item.status}`}>
                      {item.status.charAt(0).toUpperCase() +
                        item.status.slice(1)}
                    </span> */}
                    <span
                        className={`status-badge status-badge-new status-${item.status}`}
                      >
                        {item.status.charAt(0).toUpperCase() +
                           item.status.slice(1)}
                      </span>
                  </td>
                  <td className="shortDescription" title={item.createdAt
                      ? (() => {
                          const localDate = convertUtcToLocalDate(
                            item.createdAt
                          );
                          return localDate
                            ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                            : "-";
                        })()
                      : "-"}>
                    {item.createdAt
                      ? (() => {
                          const localDate = convertUtcToLocalDate(
                            item.createdAt
                          );
                          return localDate
                            ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                            : "-";
                        })()
                      : "-"}
                  </td>
                  <td className="">
                    {item.signedAt
                      ? (() => {
                          const localDate = convertUtcToLocalDate(
                            item.signedAt
                          );
                          return localDate
                            ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
                            : "-";
                        })()
                      : "-"}
                  </td>
                  <td className="action-cell">
                    {item.status === "completed" ? (
                      <button
                        className="inbox-action-btn inbox-action-btn-signed bg-[#F9F6FF] !important"
                        // disabled
                      >
                        Signed
                      </button>
                    ) : (
                        <>
                        {
                            item.status !== "rejected" && 
                             <button
                        className="inbox-action-btn inbox-action-btn-review"
                        onClick={() => {
                          navigate(`/sign/${item.requestToken}`);
                        }}
                      >
                        Sign Request
                      </button>
                        }
                        </>           
                    )}
                    {
                        item.status === "pending" && (
                            <button
                        className="inbox-action-btn inbox-action-btn-signed bg-[#F9F6FF] !important"
                        onClick={()=>handleClickCancel(item.signatureBoxID)}
                      >
                        Cancel
                      </button>
                        )
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
